import CloseIcon from "@mui/icons-material/Close";
import SupervisedUserCircleIcon from "@mui/icons-material/SupervisedUserCircle";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { Fragment } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ConfirmAndCancelButtons from "../../../../../../components/buttons/ConfirmAndCancelButtons";
import UserSelector from "../../../../../../components/form/UserSelector";
import { SnackbarContext } from "../../../../../../contexts/Snackbar";
import {
  GET_ALL_VETS,
  SET_RESPONSIBLE_VET,
} from "../../../../../../enums/ApiPaths";
import { GET_INPATIENT_STAY } from "../../../../../../enums/QueryKeys";
import { useFetch } from "../../../../../../hooks/Fetching";
import { snackbarSeverity } from "../../../../../../hooks/useSnackbar";
import api from "../../../../../../utils/api/authApi";
import ActionRow from "../../../../../admin/clinic/components/actions/ActionRow";
import UserHint from "../../../../../../components/display/UserHint";

export default function ResponsibleVet({ pet }) {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = React.useState(false);
  const methods = useForm();

  function handleDialog() {
    setOpenDialog((prev) => !prev);
    if (openDialog) methods.reset();
  }

  return (
    <>
      <ActionRow
        icon={<SupervisedUserCircleIcon />}
        label={t("ResponsibleVeterinarian")}
        description={t("ResponsibleVeterinarianDesc")}
        onClick={handleDialog}
      />

      <Dialog
        open={openDialog}
        onClose={handleDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <FormProvider {...methods}>
          <Form handleDialog={handleDialog} pet={pet} />
        </FormProvider>
      </Dialog>
    </>
  );
}

function Form({ handleDialog, pet }) {
  const { t } = useTranslation();
  const url = GET_ALL_VETS;
  const { data, isLoading, error } = useFetch(url, null, "all-vets");
  const { handleSnackbar } = React.useContext(SnackbarContext);
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useFormContext();

  async function onSubmit(data) {
    if (!data.user) {
      handleSnackbar("VeterinarianNotSelected", snackbarSeverity.ERROR);
      return;
    }

    if (!pet) {
      handleSnackbar("PetDataMissing", snackbarSeverity.ERROR);
      return;
    }

    const selectedPetId = pet.id;
    const veterinarianId = data?.user?.id;

    const res = await api.post(
      `${SET_RESPONSIBLE_VET}?id=${veterinarianId}&petId=${selectedPetId}`,
    );

    if (res) {
      await queryClient.invalidateQueries({
        queryKey: [`${GET_INPATIENT_STAY}${selectedPetId}`],
      });
      handleSnackbar("Success", snackbarSeverity.SUCCESS);
      handleDialog();
    }
  }

  if (isLoading) {
    return <LinearProgress />;
  }

  if (error) {
    return (
      <Fragment>
        <Header handleDialog={handleDialog} />
        <DialogContent>
          <Typography color="error">
            {t("FailToFetchListOfVeterinarians")}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}
        >
          <ConfirmAndCancelButtons
            confirmTitle="Save"
            cancelTitle="Cancel"
            onCancel={handleDialog}
            confirmButtonType="submit"
          />
        </DialogActions>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Header handleDialog={handleDialog} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={2}>
            <UserHint hint={"ResponsibleVeterinarianExplanation"} />
            <UserSelector
              users={data}
              showNewUserForm={false}
              label="SelectResponsibleVeterinarian"
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}
        >
          <ConfirmAndCancelButtons
            confirmTitle="Save"
            cancelTitle="Cancel"
            onCancel={handleDialog}
            confirmButtonType="submit"
          />
        </DialogActions>
      </form>
    </Fragment>
  );
}

function Header({ handleDialog }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 3,
        py: 2,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <SupervisedUserCircleIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h6" fontWeight={700}>
          {t("ResponsibleVeterinarian")}
        </Typography>
      </Stack>
      <IconButton onClick={handleDialog} size="small" color="error" title={t("Close")}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
